'use client';

import { useEffect } from 'react';
import NProgress from 'nprogress';
import StyledProgressBar from './styles';

/**
 * ProgressBar is a React component that integrates with NProgress to display
 * a progress bar at the top of the page during navigation events.
 * 
 * It configures NProgress with custom settings and adds click event listeners
 * to anchor elements with relative links. When a link is clicked, NProgress
 * starts unless the link points to the current URL. A MutationObserver is used
 * to dynamically add event listeners to newly added anchor elements.
 * 
 * The component also enhances the browser's history pushState method to
 * complete the progress bar when navigation occurs.
 * 
 * @returns {ReactElement} A styled progress bar component.
 */

export default function ProgressBar() {
  useEffect(() => {
    NProgress.configure({ easing: 'ease', speed: 500, showSpinner: false });

    /**
     * Handles click events on anchor elements with relative links.
     * 
     * @param {MouseEvent} event The click event.
     * @returns {void}
     */
    const handleAnchorClick = (event) => {
      const targetUrl = event.currentTarget.href;
      const currentUrl = window.location.href;
      if (targetUrl !== currentUrl) {
        NProgress.start();
      }
    };

    /**
     * Handles mutation events on the document to add event listeners to newly
     * added anchor elements with relative links.
     * 
     * It filters out anchor elements with non-relative links and adds the
     * handleAnchorClick event listener to the remaining anchor elements.
     * 
     * @returns {void}
     */
    const handleMutation = () => {
      const anchorElements = document.querySelectorAll('a[href]');

      const filteredAnchors = Array.from(anchorElements).filter((element) => {
        const href = element.getAttribute('href');
        return href && href.startsWith('/');
      });

      filteredAnchors.forEach((anchor) => anchor.addEventListener('click', handleAnchorClick));
    };

    const mutationObserver = new MutationObserver(handleMutation);

    mutationObserver.observe(document, { childList: true, subtree: true });

    window.history.pushState = new Proxy(window.history.pushState, {
      /**
       * Handles calls to window.history.pushState and ensures that NProgress
       * is stopped after the state change.
       *
       * @param {Function} target The original window.history.pushState function.
       * @param {Object} thisArg The value of 'this' in the target function.
       * @param {Array} argArray The arguments to pass to the target function.
       * @returns {*} The return value of the target function.
       */
      apply: (target, thisArg, argArray) => {
        NProgress.done();
        return target.apply(thisArg, argArray);
      },
    });
  });

  return <StyledProgressBar />;
}
